/**
 * Assembly queue — pick a work order before opening its work page (label print, serialized scan, or TLA BOM).
 */

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Loader2, Plus } from "lucide-react";

import { WorkOrderDetailLink } from "@/components/hub/HubEntityDetailLink";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

import {
  ASSEMBLY_FINISHED_GOOD_SKUS,
  ASSEMBLY_WORK_ORDERS_QUERY_KEY,
  fetchAssemblyWorkOrders,
} from "./assemblyApi";
import {
  assemblyProgressLabel,
  assemblyTypeBadgeTone,
  assemblyTypeLabel,
  workOrderStatusLabel,
  type AssemblyListRow,
} from "./assemblyListFormat";
import { assemblyWorkOrderPath } from "./assemblyNavigation";
import { ASSEMBLY_LABOR_FLASH_STORAGE_KEY } from "./assemblyLaborFlash";
import { CreateAssemblyWorkOrderModal } from "./CreateAssemblyWorkOrderModal";
import { statusToneForWorkOrder } from "./workOrdersPageModel";

const PAGE_HELP =
  "Open work orders at SGS whose finished good is not Made to order, including in-house reorder-point builds, build-plan assemblies, packaged reset magnets, and serialized jobs such as BABA TLA conversion.";

export function AssemblyQueuePage(): JSX.Element {
  const navigate = useNavigate();
  const writesAllowed = useHubModuleWrite("manufacturing");

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [laborReturnFlash, setLaborReturnFlash] = useState<string | null>(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(ASSEMBLY_LABOR_FLASH_STORAGE_KEY);
      if (raw != null && raw.trim() !== "") {
        sessionStorage.removeItem(ASSEMBLY_LABOR_FLASH_STORAGE_KEY);
        setLaborReturnFlash(raw.trim());
      }
    } catch {
      /* private mode or storage unavailable */
    }
  }, []);

  useEffect(() => {
    const handle = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const listQuery = useQuery({
    queryKey: [...ASSEMBLY_WORK_ORDERS_QUERY_KEY, search],
    queryFn: () => fetchAssemblyWorkOrders({ search }),
    staleTime: 15_000,
  });

  const rows = (listQuery.data?.workOrders ?? []) as AssemblyListRow[];

  function openWorkOrder(buildOrderId: number): void {
    void navigate(assemblyWorkOrderPath(buildOrderId));
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={PAGE_HELP}>Assembly</PageTitleWithHelp>
        {writesAllowed ? (
          <Button size="sm" onClick={() => setCreateModalOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            New assembly work order
          </Button>
        ) : null}
      </div>

      {laborReturnFlash ? (
        <div
          className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-900"
          role="status"
        >
          <p className="min-w-0 flex-1 leading-snug">{laborReturnFlash}</p>
          <Button size="sm" variant="secondary" type="button" onClick={() => setLaborReturnFlash(null)}>
            Dismiss
          </Button>
        </div>
      ) : null}

      <CreateAssemblyWorkOrderModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={(buildOrderId) => {
          void navigate(assemblyWorkOrderPath(buildOrderId));
        }}
      />

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="grid gap-3 md:grid-cols-[minmax(16rem,24rem)_1fr] md:items-end">
            <div>
              <Label htmlFor="assembly-search">Search work orders</Label>
              <Input
                id="assembly-search"
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Work order number, SKU, or reference…"
                autoComplete="off"
              />
            </div>
            <p className="text-sm text-slate-600">
              Open SGS work orders for TLA finished goods and {ASSEMBLY_FINISHED_GOOD_SKUS.join(" · ")} label jobs.
            </p>
          </div>
          {listQuery.data != null ? (
            <p className="text-xs text-slate-500">
              Showing {rows.length} of {listQuery.data.totalRows} work order
              {listQuery.data.totalRows !== 1 ? "s" : ""}.
            </p>
          ) : null}
        </CardBody>
      </Card>

      {listQuery.isLoading ? (
        <p className="text-sm text-slate-500">
          <Loader2 className="mr-1 inline h-4 w-4 animate-spin" aria-hidden /> Loading work orders…
        </p>
      ) : listQuery.isError ? (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {(listQuery.error as ApiError).message}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500">No assembly work orders match these filters.</p>
      ) : (
        <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white">
          {rows.map((row) => (
            <AssemblyQueueRow key={row.id} row={row} onOpen={() => openWorkOrder(row.id)} />
          ))}
        </ul>
      )}
    </div>
  );
}

function stopQueueRowOpen(event: { stopPropagation: () => void }): void {
  event.stopPropagation();
}

function AssemblyQueueRow({ row, onOpen }: { row: AssemblyListRow; onOpen: () => void }): JSX.Element {
  const reference = (row.mfr_reference_number ?? "").trim();

  return (
    <li
      role="link"
      tabIndex={0}
      aria-label={`Open assembly work order ${row.number}`}
      className="flex cursor-pointer items-center gap-2 px-4 py-3 text-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-400"
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span onClick={stopQueueRowOpen} onKeyDown={stopQueueRowOpen}>
            <WorkOrderDetailLink buildOrderId={row.id} workOrderNumber={row.number} />
          </span>
          <span className="font-mono text-slate-700">{row.sku ?? "—"}</span>
          <span className="tabular-nums text-slate-600">{assemblyProgressLabel(row)}</span>
          <AssemblyTypeBadge row={row} />
          <AssemblyStatusBadge row={row} />
        </div>
        {reference !== "" ? <p className="text-xs text-slate-500">Reference: {reference}</p> : null}
      </div>
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center text-slate-400" aria-hidden>
        <ChevronRight className="h-4 w-4" />
      </span>
    </li>
  );
}

function AssemblyStatusBadge({ row }: { row: AssemblyListRow }): JSX.Element {
  return <Badge tone={statusToneForWorkOrder(row)}>{workOrderStatusLabel(row)}</Badge>;
}

function AssemblyTypeBadge({ row }: { row: AssemblyListRow }): JSX.Element {
  return <Badge tone={assemblyTypeBadgeTone(row)}>{assemblyTypeLabel(row)}</Badge>;
}
